/**
 * Inputs: stored Thai cards + one owner write.
 * Outputs: the next card list. Adding or editing one card cannot drop the others.
 */

import { DEFAULT_THAI_PHRASES, markPhraseReviewed, normalizePhrases } from "./phrases.ts";
import { ThaiPhraseSchema, type ThaiPhrase } from "./schema.ts";

export type PhraseWrite =
  | { action: "upsert"; phrase: ThaiPhrase }
  | { action: "remove"; id: string }
  | { action: "review"; id: string; reviewed: boolean; reviewer: string }
  | { action: "restoreStarters" }
  | { action: "merge"; phrases: ThaiPhrase[]; removedIds?: string[] };

export type PhraseWriteParse =
  | { ok: true; write: PhraseWrite }
  | { ok: false; error: string };

function upsertById(current: ThaiPhrase[], phrase: ThaiPhrase): ThaiPhrase[] {
  const next = current.some((row) => row.id === phrase.id)
    ? current.map((row) => (row.id === phrase.id ? phrase : row))
    : [...current, phrase];
  return normalizePhrases(next);
}

export function applyPhraseWrite(current: ThaiPhrase[], write: PhraseWrite): ThaiPhrase[] {
  if (write.action === "upsert") return upsertById(current, write.phrase);
  if (write.action === "remove") return normalizePhrases(current.filter((row) => row.id !== write.id));
  if (write.action === "review") {
    return normalizePhrases(current.map((row) => {
      if (row.id !== write.id) return row;
      return write.reviewed
        ? markPhraseReviewed(row, write.reviewer)
        : { ...row, translationReviewed: false, reviewedBy: null, reviewedAt: null };
    }));
  }
  if (write.action === "restoreStarters") {
    const have = new Set(current.map((row) => row.id));
    const missing = DEFAULT_THAI_PHRASES.filter((row) => !have.has(row.id));
    return normalizePhrases([...current, ...missing]);
  }
  let next = current;
  for (const phrase of write.phrases) next = upsertById(next, phrase);
  if (write.removedIds?.length) {
    const drop = new Set(write.removedIds);
    next = next.filter((row) => !drop.has(row.id));
  }
  return normalizePhrases(next);
}

export function parsePhraseWrite(body: unknown): PhraseWriteParse {
  if (!body || typeof body !== "object") return { ok: false, error: "A Thai card write is required." };
  const row = body as {
    action?: unknown;
    phrase?: unknown;
    id?: unknown;
    reviewed?: unknown;
    thaiPhrases?: unknown;
    removedIds?: unknown;
  };
  const action = typeof row.action === "string" ? row.action : null;

  if (action === "upsert") {
    const parsed = ThaiPhraseSchema.safeParse(row.phrase);
    if (!parsed.success) return { ok: false, error: "A Thai card did not validate." };
    if (!parsed.data.english.trim() || !parsed.data.thai.trim()) {
      return { ok: false, error: "English and Thai are needed before this card can be saved." };
    }
    return { ok: true, write: { action: "upsert", phrase: parsed.data } };
  }
  if (action === "remove") {
    if (typeof row.id !== "string" || !row.id.trim()) return { ok: false, error: "Which card should be removed?" };
    return { ok: true, write: { action: "remove", id: row.id.trim() } };
  }
  if (action === "review") {
    if (typeof row.id !== "string" || !row.id.trim()) return { ok: false, error: "Which card should be marked?" };
    if (typeof row.reviewed !== "boolean") return { ok: false, error: "Say whether this card is reviewed." };
    return { ok: true, write: { action: "review", id: row.id.trim(), reviewed: row.reviewed, reviewer: "owner session" } };
  }
  if (action === "restoreStarters") {
    return { ok: true, write: { action: "restoreStarters" } };
  }

  if (!Array.isArray(row.thaiPhrases)) {
    return { ok: false, error: "Send one card action, or a Thai card list to merge." };
  }
  const phrases: ThaiPhrase[] = [];
  for (const item of row.thaiPhrases) {
    const parsed = ThaiPhraseSchema.safeParse(item);
    if (!parsed.success) return { ok: false, error: "A Thai card did not validate." };
    if (!parsed.data.english.trim() || !parsed.data.thai.trim()) {
      return { ok: false, error: "English and Thai are needed before this card can be saved." };
    }
    phrases.push(parsed.data);
  }
  const removedIds = Array.isArray(row.removedIds)
    ? row.removedIds.filter((id): id is string => typeof id === "string" && Boolean(id.trim()))
    : undefined;
  return { ok: true, write: { action: "merge", phrases, removedIds } };
}
